// TypeScript definitions for the check-in system

export interface SaleCheckInCustomerModel {
  businessName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
}

export interface SaleCustomer extends SaleCheckInCustomerModel {
  businessName?: string;
}

export interface SaleChild {
  id: number;
  customer: SaleCheckInCustomerModel;
  uniqueNumber: string;
  ticketName: string;
  checkedIn: boolean;
  checkedInAt?: string;
  checkedInAtLocal?: string;
}

export interface Sale {
  amount?: number;
  currency?: string;
  customer: SaleCustomer;
  details: SaleChild[];
  id: number;
  lastUpdatedAt: string;
  lastUpdatedAtLocal: string;
  receiptNumber: string;
  soldAt: string;
  soldAtLocal: string;
  checkedIn: boolean;
  checkedInAt?: string;
  checkedInAtLocal?: string;
}

export interface Campaign {
  id: number;
  name: string;
  status: string;
}

export interface ApiError {
  error: string;
  param: string;
}

export interface ApiResponse<T> {
  data: T | null;
  success: boolean;
  error?: ApiError[];
}

export enum SearchOptionsStatus {
  ALL = "all",
  CHECKED_IN = "checked-in",
  NOT_CHECKED_IN = "not-checked-in",
}

export interface SearchOptions {
  maxResults?: number;
  status?: SearchOptionsStatus;
}

export interface SearchIndexEntry {
  id: string;
  type: 'sale' | 'entry';
  parentId?: string;
  searchText: string;
  searchTerm?: string; // The actual search term that was used
  matchedField?: string;
  sale?: Sale;
  entry?: SaleChild;
  matchedEntries?: Array<{
    entry: SaleChild;
    matchedField?: string;
    searchText: string;
  }>;
}

export interface CheckInStats {
  totalSales: number;
  totalEntries: number;
  checkedInEntries: number;
  checkedInSales: number;
}

export interface DebugLog {
  timestamp: string;
  message: string;
  type: 'info' | 'error' | 'success' | 'warning';
}

export interface CheckInState {
  // Data
  sales: { [id: string]: Sale };
  entries: { [id: string]: SaleChild & { saleId: string, index: number } };
  campaign: Campaign | null;

  // Search
  searchTerm: string;
  searchResults: SearchIndexEntry[];
  searchIndex: any; // HybridSearchIndex instance

  // UI State
  isLoading: boolean;
  isInitialLoading: boolean;
  isSyncing: boolean;
  lastSyncTime: number | null;

  // Stats
  stats: CheckInStats;

  // Errors and debugging
  errors: { [key: string]: string };
  debugLogs: DebugLog[];

  // Check-in operations
  pendingCheckIns: { [key: string]: boolean };
}

export type CheckInActionType =
  | 'INIT_LOADING_START'
  | 'INIT_LOADING_SUCCESS'
  | 'INIT_LOADING_FAILURE'
  | 'SYNC_START'
  | 'SYNC_SUCCESS'
  | 'SYNC_FAILURE'
  | 'SEARCH_TERM_CHANGE'
  | 'SEARCH_RESULTS_UPDATE'
  | 'CHECK_IN_START'
  | 'CHECK_IN_SUCCESS'
  | 'CHECK_IN_FAILURE'
  | 'CHECK_IN_INDIVIDUAL_START'
  | 'CHECK_IN_INDIVIDUAL_SUCCESS'
  | 'CHECK_IN_INDIVIDUAL_FAILURE'
  | 'DEBUG_LOG_ADD'
  | 'ERROR_SET'
  | 'ERROR_CLEAR';

export interface CheckInAction {
  type: CheckInActionType;
  payload?: any;
}
